import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

// CC-205: URL-synced week for the Entry tab — same `?week=YYYY-MM-DD` contract as the Wall.
export function useUrlWeek() {
  const [searchParams, setSearchParams] = useSearchParams();
  const p = searchParams.get("week");
  const week = p && /^\d{4}-\d{2}-\d{2}$/.test(p) ? p : "this";
  const setWeek = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next && next !== "this") params.set("week", next);
    else params.delete("week");
    setSearchParams(params, { replace: true });
  };
  return [week, setWeek];
}

const BTN = "p-1.5 rounded-md disabled:text-[#c9cfd8] disabled:cursor-not-allowed hover:bg-[#f5f6f8] text-[#0e1420]";

export function EntryWeekSwitcher({ currentWeekOf, setWeek, includeEmpty = false, testIdPrefix = "entry-week" }) {
  const [weeks, setWeeks] = useState([]);
  useEffect(() => {
    api.get("/kpi/available-weeks").then(({ data }) => setWeeks((data?.weeks || []).filter((w) => includeEmpty || w.has_data !== false)))
      .catch(() => setWeeks([]));
  }, [currentWeekOf, includeEmpty]);
  const idx = weeks.findIndex((w) => w.week_of === currentWeekOf);
  const newer = idx > 0 ? weeks[idx - 1] : null;
  const older = idx > -1 && idx < weeks.length - 1 ? weeks[idx + 1] : null;
  const thisIso = weeks.find((w) => w.is_this)?.week_of;
  const onThis = !!currentWeekOf && currentWeekOf === thisIso;
  return (
    <div className="flex items-center justify-end gap-2" data-testid={`${testIdPrefix}-switch`}>
      <button type="button" onClick={() => setWeek("this")} disabled={onThis} data-testid={`${testIdPrefix}-today`}
              className="px-3 py-1.5 text-[11.5px] rounded-md font-semibold border border-[#0072bc] text-[#0072bc] bg-white hover:bg-[#e7f4fb] disabled:border-[#e7e9ee] disabled:text-[#c9cfd8] disabled:cursor-not-allowed">
        Today
      </button>
      <div className="inline-flex items-center gap-1 bg-white rounded-lg p-1 border border-[#e7e9ee]">
        <button type="button" title="Newer week" disabled={!newer} onClick={() => setWeek(newer.week_of)} data-testid={`${testIdPrefix}-newer`} className={BTN}>
          <ChevronLeft size={14} strokeWidth={2.2} />
        </button>
        <select value={idx > -1 ? currentWeekOf : ""} onChange={(e) => setWeek(e.target.value)} data-testid={`${testIdPrefix}-picker`}
                className="px-2 py-1 text-[11.5px] font-semibold text-[#0e1420] bg-white outline-none min-w-[160px]">
          {idx < 0 && <option value="">{currentWeekOf || "This Week"}</option>}
          {weeks.map((w) => <option key={w.week_of} value={w.week_of} data-testid={`${testIdPrefix}-opt-${w.week_of}`}>
            {(w.label || w.week_of) + (w.has_data === false ? " (no jobs)" : "")}
          </option>)}
        </select>
        <button type="button" title="Older week" disabled={!older} onClick={() => setWeek(older.week_of)} data-testid={`${testIdPrefix}-older`} className={BTN}>
          <ChevronRight size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}
